<template>
  <div class="chat-tratativa">
    <v-card-text class="pt-0 subheading">Histórico de tratativas</v-card-text>
    <v-divider></v-divider>

    <div class="chat-tratativa__historico" ref="historico">
      <p
        v-if="!carregando && mensagens.length === 0"
        class="chat-tratativa__vazio"
      >
        Nenhuma mensagem registrada nesta tratativa.
      </p>

      <div
        v-for="mensagem in mensagens"
        :key="chaveDaMensagem(mensagem)"
        class="chat-tratativa__linha"
        :class="ehMinha(mensagem) ? 'chat-tratativa__linha--minha' : ''"
      >
        <div class="chat-tratativa__grupo">
          <p class="chat-tratativa__autor mb-0">{{ mensagem.nomeUsuario }}</p>
          <p class="chat-tratativa__data mb-0">{{ dataDaMensagem(mensagem) }}</p>

          <div class="chat-tratativa__balao">
            <p v-if="mensagem.solicitacao || mensagem.idPropostaStatus" class="chat-tratativa__status mb-0">
              {{ tituloDoStatus(mensagem) }}
            </p>

            <pre v-if="exibeTexto(mensagem)" class="chat-tratativa__texto">{{ mensagem.mensagem }}</pre>

            <div v-if="ehLiberacaoComPdf(mensagem)" class="chat-tratativa__anexos">
              <a
                v-for="anexo in mensagem.anexos"
                :key="anexo.id"
                class="chat-tratativa__download"
                @click.prevent="baixarAnexo(anexo)"
              >
                <v-icon small class="chat-tratativa__icone-download">file_download</v-icon>
                {{ rotuloDoDownload(mensagem) }}
              </a>
            </div>

            <div v-else-if="mensagem.anexos && mensagem.anexos.length > 0" class="chat-tratativa__anexos">
              <a class="chat-tratativa__resumo-anexos" @click.prevent="alternarAnexos(mensagem)">
                <v-icon small class="chat-tratativa__clipe">attach_file</v-icon>
                {{ mensagem.anexos.length }} Anexo(s)
              </a>
              <div v-if="anexosAbertos.indexOf(chaveDaMensagem(mensagem)) !== -1" class="chat-tratativa__lista-anexos">
                <v-btn
                  v-for="anexo in mensagem.anexos"
                  :key="(anexo.daSolicitacao ? 's' : 'm') + anexo.id"
                  flat
                  small
                  class="ma-0 pa-0 chat-tratativa__anexo"
                  @click.prevent="baixarAnexo(anexo)"
                >
                  {{ anexo.originalFilename }}
                </v-btn>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <v-divider></v-divider>

    <v-container v-if="permissoes.podeEnviarMensagem" class="pb-0">
      <v-layout row wrap>
        <v-flex md4 xs12 class="pr-4">
          <v-select
            label="Status"
            browser-autocomplete="off"
            placeholder="Selecione"
            item-text="titulo"
            item-value="id"
            :clearable="podeTrocarStatus"
            :disabled="!podeTrocarStatus"
            :items="opcoesDeStatus"
            v-model="statusSelecionado"
          ></v-select>
        </v-flex>

        <v-flex class="md6 xs12">
          <v-text-field
            label="Mensagem"
            placeholder="Escrever tratativa"
            browser-autocomplete="off"
            v-model="texto"
            :error-messages="erro ? [erro] : []"
            @keyup.enter="enviar"
          ></v-text-field>
        </v-flex>

        <v-flex md2 xs12 class="text-xs-right">
          <v-btn
            icon
            flat
            v-if="permissoes.podeAnexar"
            :title="'Anexar arquivo'"
            @click.prevent="abrirSeletorDeArquivo"
          >
            <v-icon>attach_file</v-icon>
          </v-btn>
          <v-btn icon flat color="primary" :disabled="enviando" @click.prevent="enviar">
            <v-icon>send</v-icon>
          </v-btn>
          <input
            ref="seletorArquivo"
            type="file"
            multiple
            class="chat-tratativa__input-arquivo"
            @change="selecionarArquivos"
          />
        </v-flex>

        <v-flex xs12 v-if="arquivos.length > 0">
          <v-chip
            v-for="(arquivo, indice) in arquivos"
            :key="indice"
            close
            small
            @input="removerArquivo(indice)"
          >
            {{ arquivo.name }}
          </v-chip>
        </v-flex>
      </v-layout>
    </v-container>

    <v-card-text v-else-if="habilitada === false" class="chat-tratativa__bloqueado">
      Esta cotação é anterior ao chat de tratativas: só é possível consultar a solicitação e os anexos enviados.
    </v-card-text>

    <v-card-text v-else class="chat-tratativa__bloqueado">
      Esta tratativa está encerrada. O histórico permanece disponível para consulta.
    </v-card-text>
  </div>
</template>

<script>
import axios from "axios";
import usuarioHelper from "@/helpers/usuario-logado.js";
import statusTratativa from "@/helpers/status-tratativa.js";

const STATUS_LIBERADA = 13;

export default {
  name: "ChatTratativa",

  props: {
    idCorretorReserva: {
      type: [Number, String],
      default: null,
    },
    idApolice: {
      type: [Number, String],
      default: null,
    },
  },

  data() {
    return {
      carregando: false,
      enviando: false,
      erro: "",
      texto: "",
      idPropostaStatusSelecionado: null,
      arquivos: [],
      anexosAbertos: [],
      mensagens: [],
      idPropostaStatus: null,
      idCorretorReservaResolvido: null,
      habilitada: null,
      permissoes: {
        podeEnviarMensagem: false,
        podeAnexar: false,
        statusDisponiveis: [],
        statusComMensagemObrigatoria: [],
      },
    };
  },

  computed: {
    idPerfil() {
      const usuario = usuarioHelper.USUARIO_LOGADO();
      return usuario && usuario.perfil ? usuario.perfil.id : null;
    },
    idUsuario() {
      const usuario = usuarioHelper.USUARIO_LOGADO();
      return usuario ? usuario.idUsuario : null;
    },
    podeTrocarStatus() {
      return (this.permissoes.statusDisponiveis || []).length > 0;
    },
    opcoesDeStatus() {
      if (!this.podeTrocarStatus) {
        return this.idPropostaStatus
          ? [{ id: this.idPropostaStatus, titulo: this.rotuloDoStatus(this.idPropostaStatus) }]
          : [];
      }
      return (this.permissoes.statusDisponiveis || []).map((id) => ({
        id,
        titulo: statusTratativa.rotuloStatus(id, this.idPerfil),
      }));
    },
    statusSelecionado: {
      get() {
        return this.podeTrocarStatus ? this.idPropostaStatusSelecionado : this.idPropostaStatus;
      },
      set(valor) {
        this.idPropostaStatusSelecionado = valor;
      },
    },
    cabecalho() {
      return {
        Authorization: `Bearer ${usuarioHelper.USUARIO_LOGADO().token}`,
      };
    },
  },

  watch: {
    idCorretorReserva() {
      this.carregar();
    },
    idApolice() {
      this.carregar();
    },
  },

  mounted() {
    this.carregar();
  },

  methods: {
    rotuloDoStatus(idStatus) {
      return statusTratativa.rotuloStatus(idStatus, this.idPerfil);
    },

    chaveDaMensagem(mensagem) {
      return mensagem.solicitacao ? "solicitacao" : mensagem.id;
    },

    dataDaMensagem(mensagem) {
      if (!mensagem.somenteData || !mensagem.dataHora) {
        return this.formatarData(mensagem.dataHora);
      }
      const data = new Date(mensagem.dataHora);
      return isNaN(data.getTime()) ? mensagem.dataHora : data.toLocaleDateString("pt-BR");
    },

    tituloDoStatus(mensagem) {
      if (mensagem.solicitacao) return "Cotação Solicitada";
      const rotulo = this.rotuloDoStatus(mensagem.idPropostaStatus);
      if (!rotulo) return "";
      const base = rotulo.toLowerCase().indexOf("cota") === 0 ? rotulo : `Cotação ${rotulo}`;
      return mensagem.numeroVersao ? `${base} V${mensagem.numeroVersao}` : base;
    },

    exibeTexto(mensagem) {
      if (!mensagem.mensagem || !mensagem.mensagem.trim()) return false;
      if (!mensagem.idPropostaStatus) return true;
      return mensagem.mensagem.trim() !== this.tituloDoStatus(mensagem);
    },

    ehLiberacaoComPdf(mensagem) {
      return (
        !mensagem.solicitacao &&
        mensagem.idPropostaStatus === STATUS_LIBERADA &&
        mensagem.anexos &&
        mensagem.anexos.length > 0
      );
    },

    rotuloDoDownload(mensagem) {
      return mensagem.numeroVersao
        ? `Baixar PDF da cotação V${mensagem.numeroVersao}`
        : "Baixar PDF da cotação";
    },

    alternarAnexos(mensagem) {
      const chave = this.chaveDaMensagem(mensagem);
      const indice = this.anexosAbertos.indexOf(chave);
      if (indice === -1) {
        this.anexosAbertos.push(chave);
        return;
      }
      this.anexosAbertos.splice(indice, 1);
    },

    ehMinha(mensagem) {
      return mensagem.idUsuario === this.idUsuario;
    },

    formatarData(dataHora) {
      if (!dataHora) return "";
      const data = new Date(dataHora);
      if (isNaN(data.getTime())) return dataHora;
      return data.toLocaleString("pt-BR");
    },

    carregar() {
      if (!this.idCorretorReserva && !this.idApolice) return;

      const url = this.idCorretorReserva
        ? `${process.env.URL_TRATATIVAMS}/v1/${this.idCorretorReserva}`
        : `${process.env.URL_TRATATIVAMS}/v1/por-apolice/${this.idApolice}`;

      this.carregando = true;
      return axios({ url, method: "GET", headers: this.cabecalho })
        .then((resposta) => {
          this.aplicarTratativa(resposta.data);
          this.carregando = false;
          this.$emit("carregada", resposta.data);
        })
        .catch(() => {
          this.carregando = false;
          this.erro = "Não foi possível carregar a tratativa.";
        });
    },

    aplicarTratativa(tratativa) {
      this.mensagens = tratativa.mensagens || [];
      this.idPropostaStatus = tratativa.idPropostaStatus;
      this.idCorretorReservaResolvido = tratativa.idCorretorReserva;
      this.permissoes = tratativa.permissoes || this.permissoes;
      this.habilitada = tratativa.habilitada;
      this.$nextTick(this.rolarParaAUltima);
    },

    rolarParaAUltima() {
      const historico = this.$refs.historico;
      if (historico) {
        historico.scrollTop = historico.scrollHeight;
      }
    },

    abrirSeletorDeArquivo() {
      this.$refs.seletorArquivo.click();
    },

    selecionarArquivos(evento) {
      this.arquivos = Array.from(evento.target.files || []);
      evento.target.value = "";
    },

    removerArquivo(indice) {
      this.arquivos.splice(indice, 1);
    },

    exigeJustificativa(idStatus) {
      const doServidor = this.permissoes.statusComMensagemObrigatoria;
      if (doServidor && doServidor.length > 0) {
        return doServidor.indexOf(idStatus) !== -1;
      }
      return statusTratativa.mensagemObrigatoria(idStatus);
    },

    validar() {
      const semTexto = !this.texto || !this.texto.trim();
      if (!this.idPropostaStatusSelecionado && semTexto && this.arquivos.length === 0) {
        this.erro = "Informe uma mensagem, anexe um arquivo ou selecione um status.";
        return false;
      }
      if (this.exigeJustificativa(this.idPropostaStatusSelecionado) && semTexto) {
        this.erro = "Informe a justificativa para concluir esta ação.";
        return false;
      }
      this.erro = "";
      return true;
    },

    enviar() {
      if (this.enviando || !this.validar()) return;

      const idCorretorReserva = this.idCorretorReserva || this.idCorretorReservaResolvido;
      if (!idCorretorReserva) return;

      const dados = new FormData();
      if (this.texto && this.texto.trim()) dados.append("mensagem", this.texto.trim());
      if (this.idPropostaStatusSelecionado) {
        dados.append("idPropostaStatus", this.idPropostaStatusSelecionado);
      }
      this.arquivos.forEach((arquivo) => dados.append("arquivos", arquivo));

      this.enviando = true;
      this.$store.commit("startLoading");
      return axios({
        url: `${process.env.URL_TRATATIVAMS}/v1/${idCorretorReserva}/mensagem`,
        method: "POST",
        data: dados,
        headers: this.cabecalho,
      })
        .then((resposta) => {
          this.texto = "";
          this.idPropostaStatusSelecionado = null;
          this.arquivos = [];
          this.aplicarTratativa(resposta.data);
          this.$emit("enviada", resposta.data);
        })
        .catch((err) => {
          this.erro =
            err && err.response && err.response.data && err.response.data.message
              ? err.response.data.message
              : "Não foi possível enviar a mensagem.";
        })
        .then(() => {
          this.enviando = false;
          this.$store.commit("endLoading");
        });
    },

    baixarAnexo(anexo) {
      this.$store.commit("startLoading");
      return axios({
        url: anexo.daSolicitacao
          ? `${process.env.URL_TRATATIVAMS}/v1/anexo-solicitacao/${anexo.id}`
          : `${process.env.URL_TRATATIVAMS}/v1/anexo/${anexo.id}`,
        method: "GET",
        responseType: "blob",
        headers: this.cabecalho,
      })
        .then((resposta) => {
          const blob = new Blob([resposta.data], { type: anexo.contentType });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.download = anexo.originalFilename;
          link.href = url;
          link.click();
        })
        .then(() => this.$store.commit("endLoading"))
        .catch(() => this.$store.commit("endLoading"));
    },
  },
};
</script>

<style scoped>
.chat-tratativa__historico {
  min-height: 280px;
  max-height: 340px;
  overflow-y: auto;
  padding: 12px 16px;
}

.chat-tratativa__vazio {
  color: #9e9e9e;
  text-align: center;
  margin-top: 24px;
}

.chat-tratativa__linha {
  display: flex;
  margin-bottom: 10px;
}

.chat-tratativa__linha--minha {
  justify-content: flex-end;
}

.chat-tratativa__grupo {
  max-width: 70%;
}

.chat-tratativa__linha--minha .chat-tratativa__grupo {
  text-align: right;
}

.chat-tratativa__balao {
  display: inline-block;
  text-align: left;
  background-color: #ffffff;
  border-radius: 12px;
  padding: 10px 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
}

.chat-tratativa__autor {
  font-size: 13px;
  color: #9e9e9e;
  font-weight: 600;
}

.chat-tratativa__data {
  font-size: 11px;
  color: #9e9e9e;
  margin-bottom: 4px !important;
}

.chat-tratativa__status {
  font-size: 14px;
  font-weight: 700;
  color: #00456e;
}

.chat-tratativa__texto {
  font-family: inherit;
  font-size: 13px;
  white-space: pre-wrap;
  word-break: break-word;
  margin: 4px 0 0 0;
}

.chat-tratativa__anexos {
  margin-top: 4px;
}

.chat-tratativa__resumo-anexos {
  font-size: 13px;
  color: #696969;
  text-decoration: none;
}

.chat-tratativa__clipe {
  color: #00456e;
}

.chat-tratativa__download {
  display: inline-flex;
  align-items: center;
  font-size: 13px;
  font-weight: 600;
  color: #00456e;
  text-decoration: none;
}

.chat-tratativa__icone-download {
  color: #00456e;
  margin-right: 4px;
}

.chat-tratativa__lista-anexos {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-top: 4px;
}

.chat-tratativa__lista-anexos .chat-tratativa__anexo {
  text-transform: none;
  font-size: 12px;
  height: auto;
  min-height: 24px;
  margin-bottom: 2px !important;
}

.chat-tratativa__input-arquivo {
  display: none;
}

.chat-tratativa__bloqueado {
  color: #9e9e9e;
  font-size: 13px;
}
</style>



// WEBPACK FOOTER //
// src/components/pages/tratativa/ChatTratativa.vue